import { FORM_ERROR } from 'final-form';
import type { AnyAction, Dispatch } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import * as ActionTypes from '../../constants';
import browserHistory from '../../browserHistory';
import {
  signUp,
  logIn,
  logOut,
  sendPasswordReset,
  sendVerificationEmail,
  updateUserAccount,
  subscribeToAuthChanges,
  refreshAuthUser
} from '../../firebase/auth';
import { justOpenedProject } from '../IDE/actions/ide';
import { showToast, setToastText } from '../IDE/actions/toast';
import type { GetRootState, RootState } from '../../reducers';

export function authError(error: unknown) {
  return { type: ActionTypes.AUTH_ERROR, payload: error };
}

export function setPreferences(preferences: Record<string, unknown>) {
  return { type: ActionTypes.SET_PREFERENCES, preferences };
}

/** Start the Firebase auth-state listener; mirror sign-in/out into Redux. Call once at app boot. */
export function initAuthListener() {
  return (dispatch: Dispatch) => subscribeToAuthChanges(dispatch);
}

export function validateAndLoginUser(formProps: {
  email: string;
  password: string;
}) {
  return (
    dispatch: ThunkDispatch<RootState, unknown, AnyAction>,
    getState: GetRootState
  ) => {
    const { previousPath } = getState().ide;
    return logIn(formProps.email, formProps.password)
      .then(() => {
        dispatch(justOpenedProject());
        browserHistory.push(previousPath);
        // Positive confirmation — login now requires a verified email, so a successful login is also the
        // "your email is verified" success. The Toast component runs the key through t().
        dispatch(showToast(5500));
        dispatch(setToastText('Toast.LoginSuccess'));
      })
      .catch((error) => ({ [FORM_ERROR]: friendlyLoginError(error) }));
  };
}

// Map common Firebase Auth sign-in error codes to an i18n KEY (the LoginForm runs it through t(), so the
// message follows the displayed language). The unverified-email case is ours (thrown by logIn), the rest
// are Firebase codes. Unknown errors fall back to the raw Firebase message.
function friendlyLoginError(error: {
  code?: string;
  message?: string;
}): string {
  switch (error?.code) {
    case 'auth/email-not-verified':
      return 'LoginForm.Errors.emailNotVerified';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-email':
      return 'LoginForm.Errors.invalidCredentials';
    case 'auth/too-many-requests':
      return 'LoginForm.Errors.tooManyRequests';
    case 'auth/network-request-failed':
      return 'LoginForm.Errors.network';
    default:
      return error?.message || 'LoginForm.Errors.generic';
  }
}

export function validateAndSignUpUser(formValues: {
  username: string;
  email: string;
  password: string;
}) {
  return (dispatch: ThunkDispatch<RootState, unknown, AnyAction>) =>
    signUp(formValues.username, formValues.email, formValues.password)
      .then(async () => {
        // signUp creates the account, sends a verification email, and leaves the new (unverified) user
        // signed in. Sign them back out and send them to the login page with a clear "verify first"
        // message — they can't log in until verified (see logIn), so dropping them into the editor would
        // be a dead end.
        await logOut().catch(() => {});
        browserHistory.push('/login');
        dispatch(showToast(9000));
        dispatch(
          // The Toast component runs this through t(), so it follows the displayed language.
          setToastText('Toast.AccountCreatedVerify')
        );
      })
      .catch((error) => ({ [FORM_ERROR]: friendlySignupError(error) }));
}

// Map common signup error codes to an i18n KEY (the SignupForm runs it through t(), so the message
// follows the displayed language). Unknown errors fall back to the raw Firebase message.
function friendlySignupError(error: {
  code?: string;
  message?: string;
}): string {
  switch (error?.code) {
    case 'auth/email-already-in-use':
      return 'SignupForm.Errors.emailInUse';
    case 'auth/invalid-email':
      return 'SignupForm.Errors.invalidEmail';
    case 'auth/weak-password':
      return 'SignupForm.Errors.weakPassword';
    case 'auth/network-request-failed':
      return 'SignupForm.Errors.network';
    case 'app/username-taken':
      return 'SignupForm.Errors.usernameTaken';
    default:
      return error?.message || 'SignupForm.Errors.generic';
  }
}

export function logoutUser() {
  return (dispatch: Dispatch) =>
    logOut()
      .then(() => {
        dispatch({ type: ActionTypes.UNAUTH_USER });
        dispatch({ type: ActionTypes.RESET_PROJECT });
        dispatch({ type: ActionTypes.CLEAR_CONSOLE });
        browserHistory.push('/');
      })
      .catch((error) => dispatch(authError(error)));
}

export function initiateResetPassword(formValues: { email: string }) {
  return (dispatch: ThunkDispatch<RootState, unknown, AnyAction>) => {
    dispatch({ type: ActionTypes.RESET_PASSWORD_INITIATE });
    return sendPasswordReset(formValues.email).catch((error) => {
      // ActionTypes.ERROR has no reducer — surface a toast so a send failure isn't silent.
      dispatch(showToast(5500));
      dispatch(
        setToastText('Could not send the reset email. Please try again.')
      );
      return { error };
    });
  };
}

export function resetPasswordReset() {
  return { type: ActionTypes.RESET_PASSWORD_RESET };
}

// Re-read the Firebase user (refreshing the token) so a stale emailVerified flag self-corrects, e.g.
// when the user returns to /account after clicking the verification link. Errors are non-fatal.
export function refreshUser() {
  return (dispatch: Dispatch) => refreshAuthUser(dispatch).catch(() => {});
}

export function initiateVerification() {
  return (dispatch: ThunkDispatch<RootState, unknown, AnyAction>) => {
    dispatch({ type: ActionTypes.EMAIL_VERIFICATION_INITIATE });
    return sendVerificationEmail().catch((error) => {
      dispatch(showToast(5500));
      dispatch(
        setToastText('Could not send the verification email. Please try again.')
      );
    });
  };
}

export function updateSettings(formValues: {
  email?: string;
  newPassword?: string;
}) {
  return (dispatch: ThunkDispatch<RootState, unknown, AnyAction>) =>
    updateUserAccount(formValues)
      .then(() => {
        if (formValues.email) {
          dispatch({
            type: ActionTypes.SETTINGS_UPDATED,
            user: { email: formValues.email }
          });
        }
        dispatch(showToast(5500));
        dispatch(setToastText('Toast.SettingsSaved'));
      })
      .catch((error: { code?: string }) => {
        dispatch(showToast(5500));
        // Firebase requires a recent login to change email/password. Tell the user exactly what to
        // do instead of a generic "something went wrong" (a full in-place reauthenticateWithCredential
        // prompt is a possible future enhancement).
        dispatch(
          setToastText(
            error?.code === 'auth/requires-recent-login'
              ? 'For security, please log out and back in, then change your email or password again.'
              : 'Toast.DefaultError'
          )
        );
      });
}

/**
 * Reflect a just-created/just-joined classroom as the user's current one in Redux. The server
 * (createClassroom / joinClassroom callables) also persists this to users/{uid}; this dispatch
 * updates the top-bar tag immediately, without waiting for the next sign-in profile read.
 */
export function setCurrentClassroom(classroomId: string, name: string) {
  return {
    type: ActionTypes.SET_CURRENT_CLASSROOM,
    currentClassroom: { id: classroomId, name }
  };
}

export function setUserCookieConsent(cookieConsent: string) {
  // Client-only now (no server). Persist to Redux; the consent component keeps its local state.
  return (dispatch: Dispatch) => {
    dispatch({ type: ActionTypes.SET_COOKIE_CONSENT, cookieConsent });
  };
}
